// Centralized option lists for selects across CompanyDetail, filters and import.

export interface Option {
  value: string;
  label: string;
}

export interface OptionGroup {
  label: string;
  tone?: "neutral" | "warning";
  options: FounderOriginOption[];
}

export interface FounderOriginOption extends Option {
  /** Indicative point value (display hint only — the backend is the source of truth). */
  hint?: string;
  /** Renders the option as an exclusion (rose styling). */
  excluded?: boolean;
}

// ─── Funding stages ──────────────────────────────────────────────────────

export const FUNDING_STAGES: Option[] = [
  { value: "pre_seed", label: "Pre-Seed" },
  { value: "seed", label: "Seed" },
  { value: "series_a", label: "Series A" },
  { value: "series_b", label: "Series B" },
  { value: "series_c", label: "Series C+" },
];

// ─── Founder origins ─────────────────────────────────────────────────────

export const FOUNDER_ORIGIN_GROUPS: OptionGroup[] = [
  {
    label: "High affinity (8–10 pts)",
    options: [
      { value: "french", label: "🇫🇷 French", hint: "10 pts" },
      {
        value: "north_african",
        label: "🇲🇦 North African (Maghreb)",
        hint: "10 pts",
      },
      {
        value: "sub_saharan_african",
        label: "🌍 Sub-Saharan African",
        hint: "8 pts",
      },
      {
        value: "european",
        label: "🇪🇺 European (non-French)",
        hint: "8 pts",
      },
    ],
  },
  {
    label: "Strong affinity (6–7 pts)",
    options: [
      { value: "israeli", label: "🇮🇱 Israeli", hint: "7 pts" },
      { value: "indian", label: "🇮🇳 Indian", hint: "7 pts" },
      {
        value: "east_asian",
        label: "🇨🇳 East Asian (China, Japan, Korea, Taiwan)",
        hint: "6 pts",
      },
      {
        value: "southeast_asian",
        label: "🇻🇳 SE Asian (Vietnam, Philippines, Indonesia, Thailand)",
        hint: "6 pts",
      },
      {
        value: "latam",
        label: "🇧🇷 Latin American (Brazil, Mexico, Argentina, Colombia)",
        hint: "6 pts",
      },
      {
        value: "middle_eastern",
        label: "🇱🇧 Middle Eastern (non-Israeli)",
        hint: "6 pts",
      },
    ],
  },
  {
    label: "Moderate affinity (5 pts)",
    options: [
      { value: "canadian", label: "🇨🇦 Canadian", hint: "5 pts" },
      { value: "other_intl", label: "🌍 Other International", hint: "5 pts" },
    ],
  },
  {
    label: "No score",
    options: [{ value: "unknown", label: "❓ Unknown", hint: "0 pts" }],
  },
  {
    label: "Excluded",
    tone: "warning",
    options: [
      {
        value: "us_native",
        label: "🇺🇸 United States Native",
        hint: "excluded",
        excluded: true,
      },
    ],
  },
];

export const FOUNDER_ORIGIN_FLAT: FounderOriginOption[] =
  FOUNDER_ORIGIN_GROUPS.flatMap((g) => g.options);

// Default sub-score points per origin, parsed from the hint strings.
// Used when a project's saved rule has no sub_scores yet.
export const FOUNDER_ORIGIN_DEFAULT_SUB_SCORES: Record<string, number> =
  FOUNDER_ORIGIN_FLAT.reduce<Record<string, number>>((acc, opt) => {
    if (opt.excluded) return acc;
    const match = (opt.hint ?? "").match(/(\d+)/);
    if (match) acc[opt.value] = Number(match[1]);
    return acc;
  }, {});

export function lookupFounderOrigin(
  value: string | null | undefined,
): FounderOriginOption | null {
  if (!value) return null;
  return FOUNDER_ORIGIN_FLAT.find((o) => o.value === value) ?? null;
}

// ─── Hiring signals — default sub_scores ─────────────────────────────────

/** Default sub_scores for the `signaux_recrutement` scoring rule.
 *  Used as a seed when the project hasn't shipped a sub_scores
 *  override yet — both Settings → Scoring (the editor) and the
 *  Company detail Hiring signals card (the operator-facing
 *  display) read from this same constant so the UX stays in
 *  lockstep. The numbers match the production seed. */
export const HIRING_SIGNALS_DEFAULT_SUB_SCORES: Record<string, number> = {
  is_hiring_finance: 15,
  is_hiring_cos_coo: 12,
  is_hiring_hr_admin: 8,
  headcount_growth_rate: 5,
};

// ─── Countries ───────────────────────────────────────────────────────────

export const COUNTRIES: Option[] = [
  { value: "US", label: "🇺🇸 United States" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "FR", label: "🇫🇷 France" },
  { value: "DE", label: "🇩🇪 Germany" },
  { value: "IL", label: "🇮🇱 Israel" },
  { value: "IN", label: "🇮🇳 India" },
  { value: "BR", label: "🇧🇷 Brazil" },
  { value: "SG", label: "🇸🇬 Singapore" },
  { value: "AU", label: "🇦🇺 Australia" },
  { value: "NL", label: "🇳🇱 Netherlands" },
  { value: "SE", label: "🇸🇪 Sweden" },
  { value: "IE", label: "🇮🇪 Ireland" },
  { value: "JP", label: "🇯🇵 Japan" },
  { value: "KR", label: "🇰🇷 South Korea" },
  { value: "MX", label: "🇲🇽 Mexico" },
  { value: "AR", label: "🇦🇷 Argentina" },
  { value: "CO", label: "🇨🇴 Colombia" },
];

export function lookupCountry(value: string | null | undefined): Option | null {
  if (!value) return null;
  return COUNTRIES.find((c) => c.value === value) ?? null;
}

// ─── US states (most common for B2B prospecting) ─────────────────────────

export const US_STATES: Option[] = [
  { value: "CA", label: "California" },
  { value: "NY", label: "New York" },
  { value: "TX", label: "Texas" },
  { value: "MA", label: "Massachusetts" },
  { value: "WA", label: "Washington" },
  { value: "FL", label: "Florida" },
  { value: "IL", label: "Illinois" },
  { value: "CO", label: "Colorado" },
  { value: "GA", label: "Georgia" },
  { value: "PA", label: "Pennsylvania" },
  { value: "NC", label: "North Carolina" },
  { value: "VA", label: "Virginia" },
  { value: "OH", label: "Ohio" },
  { value: "AZ", label: "Arizona" },
  { value: "OR", label: "Oregon" },
  { value: "CT", label: "Connecticut" },
  { value: "NJ", label: "New Jersey" },
  { value: "MD", label: "Maryland" },
  { value: "UT", label: "Utah" },
  { value: "MN", label: "Minnesota" },
];

export function lookupUSState(
  value: string | null | undefined,
): Option | null {
  if (!value) return null;
  return US_STATES.find((s) => s.value === value) ?? null;
}

